/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch, useRouteMatch } from 'react-router';
import { NotFoundRedirect } from 'src/core/components/NotFoundRoute';
import { PrivateRoute } from 'src/core/components/PrivateRoute';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { getStylist } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { CustomTextProjectPage } from 'src/customText/appShell/CustomTextProjectPage';
import { MyStoragePage } from 'src/customText/appShell/MyStoragePage';
import { CustomTextProjectsListPage } from 'src/customText/appShell/projectsListPage/CustomTextProjectsListPage';
import { CustomTextBreadcrumb } from 'src/customText/core/components/CustomTextBreadcrumb';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { CustomTextSidebar } from 'src/customText/modules/components/CustomTextSidebar';

const { styleDiv, styleComponent } = getStylist('CustomTextRoutes');
const BreadcrumbWrapper = styleDiv('CustomTextBreadcrumbWrapper', { paddingLeft: 24 });
const CustomTextRoutesContainer = styleComponent<IStackProps>(Stack)('CustomTextRoutesContainer', { overflow: 'hidden' });
const CustomTextContentContainer = styleComponent<IStackProps>(Stack)('CluContentContainer', { overflow: 'hidden' });

export const CustomTextRoutes = observer(() => {
    const { rootStore, dispatcher } = useGlobalState();
    const { isSignedIn } = rootStore.authStore;
    const project = rootStore.currentCustomTextProjectStore;
    const isNotDataTaggingPage = useRouteMatch(routeTable.customText.dataTaggingPage(project?.id, project?.projectKind)) === null;
    const isNotProjectListPage = !useRouteMatch(routeTable.customText.projectsPage())?.isExact;

    const renderCustomTextProjectPage = (props: RouteComponentProps<{ projectId: string }>, projectKind: CustomTextProjectKind) => {
        const { params } = props.match;

        return <CustomTextProjectPage projectId={params.projectId} projectKind={projectKind} />;
    };

    React.useEffect(() => () => dispatcher.dispatch('resetCustomTextCultureStoreHouseTracker'), []);

    return (
        <CustomTextRoutesContainer horizontal grow>
            {isNotProjectListPage && <CustomTextSidebar />}

            <CustomTextContentContainer grow>
                {isNotDataTaggingPage && (
                    <BreadcrumbWrapper>
                        <CustomTextBreadcrumb />
                    </BreadcrumbWrapper>
                )}

                <Switch>
                    {!isSignedIn && [
                        <Route
                            component={() => <CustomTextProjectsListPage areExtractionProjects />}
                            path={routeTable.customText.extractionProjectsPage()}
                        />,
                        <Route
                            path={routeTable.customText.classificationProjectsPage()}
                            component={() => <CustomTextProjectsListPage areExtractionProjects={false} />}
                        />
                    ]}

                    <PrivateRoute
                        exact
                        path={routeTable.customText.extractionProjectsPage()}
                        component={() => <CustomTextProjectsListPage areExtractionProjects />}
                    />
                    <PrivateRoute
                        exact
                        path={routeTable.customText.classificationProjectsPage()}
                        component={() => <CustomTextProjectsListPage areExtractionProjects={false} />}
                    />
                    <PrivateRoute path={routeTable.customText.myStoragePage()} component={MyStoragePage} />
                    <PrivateRoute
                        path={routeTable.customText.extractionProjectPage()}
                        render={(props: RouteComponentProps<{ projectId: string }>) =>
                            renderCustomTextProjectPage(props, 'CustomEntityRecognition')
                        }
                    />
                    <PrivateRoute
                        path={routeTable.customText.classificationProjectPage()}
                        render={(props: RouteComponentProps<{ projectId: string }>) =>
                            renderCustomTextProjectPage(props, 'CustomSingleLabelClassification')
                        }
                    />

                    <Redirect from={routeTable.customText.root()} to={routeTable.customText.extractionProjectsPage()} />
                    <NotFoundRedirect />
                </Switch>
            </CustomTextContentContainer>
        </CustomTextRoutesContainer>
    );
});
